/**
 * Pure layout / placement helpers for the scheduling overview calendar.
 * Extracted from SchedulingOverviewCalendar.js — no React, no module state.
 */

/**
 * Visible hour range for day timeline (whole hours). Includes partial blackout
 * windows so they remain on-screen.
 *
 * @param {Array<{ start: Date, end: Date, kind?: string }>} rows
 * @param {Array<{ start: Date, end: Date }>} [partialBlackoutRows]
 * @returns {{ startHour: number, endHourExclusive: number }}
 */
export function computeDayTimelineRange(rows, partialBlackoutRows = []) {
  const fallback = { startHour: 7, endHourExclusive: 20 };
  const real = rows.filter(
    (r) =>
      r.start &&
      r.end &&
      r.kind !== "blackout_summary" &&
      !Number.isNaN(r.start.getTime()) &&
      !Number.isNaN(r.end.getTime()),
  );
  const partial = partialBlackoutRows.filter(
    (r) =>
      r.start &&
      r.end &&
      !Number.isNaN(r.start.getTime()) &&
      !Number.isNaN(r.end.getTime()),
  );
  const combined = [...real, ...partial];
  if (combined.length === 0) return fallback;
  let minT = Infinity;
  let maxT = -Infinity;
  for (const r of combined) {
    minT = Math.min(minT, r.start.getTime());
    maxT = Math.max(maxT, r.end.getTime());
  }
  const day0 = new Date(combined[0].start);
  day0.setHours(0, 0, 0, 0);
  const day0Ms = day0.getTime();
  const minH = Math.floor((minT - day0Ms) / 3600000);
  const maxH = Math.ceil((maxT - day0Ms) / 3600000);
  const startHour = Math.max(0, minH - 1);
  const endHourExclusive = Math.min(24, Math.max(maxH + 1, startHour + 6));
  return { startHour, endHourExclusive };
}

export function dayTimelinePositionStyle(ev, rangeStartMs, totalMin) {
  const t0 = ev.start.getTime();
  const t1 = ev.end.getTime();
  const durMin = Math.max(1, (t1 - t0) / 60000);
  const topMin = Math.max(0, (t0 - rangeStartMs) / 60000);
  const topPct = (topMin / totalMin) * 100;
  const hPct = (durMin / totalMin) * 100;
  return {
    top: `${topPct}%`,
    height: `${Math.max(hPct, 1.1)}%`,
  };
}

/**
 * Greedy column assignment for overlapping events. Returns a column index per
 * event plus the total number of columns the event must share with.
 */
export function computeOverlapLayout(rows) {
  if (rows.length === 0) return { colMap: new Map(), totalColsMap: new Map() };
  const kindPriority = (k) => (k === "class" ? 0 : 1);
  const sorted = [...rows].sort((a, b) => {
    const dt = a.start.getTime() - b.start.getTime();
    if (dt !== 0) return dt;
    const kd = kindPriority(a.kind) - kindPriority(b.kind);
    if (kd !== 0) return kd;
    return b.end.getTime() - a.end.getTime();
  });
  const colMap = new Map();
  const colEnds = [];
  for (const ev of sorted) {
    const t0 = ev.start.getTime();
    let col = 0;
    while (col < colEnds.length && colEnds[col] > t0) col++;
    colMap.set(ev.key, col);
    colEnds[col] = ev.end.getTime();
  }
  const totalColsMap = new Map();
  for (const ev of sorted) {
    const t0 = ev.start.getTime();
    const t1 = ev.end.getTime();
    let maxCol = colMap.get(ev.key);
    for (const other of sorted) {
      if (other.key === ev.key) continue;
      if (other.start.getTime() < t1 && other.end.getTime() > t0) {
        maxCol = Math.max(maxCol, colMap.get(other.key));
      }
    }
    totalColsMap.set(ev.key, maxCol + 1);
  }
  return { colMap, totalColsMap };
}

export function getScrollableAncestors(el) {
  const out = [];
  let p = el?.parentElement;
  while (p) {
    const s = getComputedStyle(p);
    const oy = s.overflowY;
    const ox = s.overflowX;
    if (/(auto|scroll|overlay)/.test(oy) || /(auto|scroll|overlay)/.test(ox)) {
      out.push(p);
    }
    p = p.parentElement;
  }
  return out;
}

/**
 * Place anchor just below the full black pill; horizontal center on the "?" icon.
 * Returns viewport coords for a zero-size `position:fixed` root (no transform on that node).
 */
export function measureMonthBlackoutPopoverPlacement(rowEl, btnEl) {
  if (!rowEl || !btnEl) return null;
  const rowR = rowEl.getBoundingClientRect();
  const btnR = btnEl.getBoundingClientRect();
  const gap = 8;
  const top = rowR.bottom + gap;
  const left = btnR.left + btnR.width / 2;
  return { top, left };
}
